import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentLauncher from '../assessments/AssessmentLauncher';
import AssessmentRunner from '../assessments/AssessmentRunner';
import ErrorBoundary from '../shared/ErrorBoundary';

/**
 * AssessmentManager Component
 * Manages assessment launcher and runner dialogs with state
 *
 * @param {Object} props - Component props
 * @param {string} props.token - Authentication token
 * @param {string} props.currentConversationId - Current conversation ID
 * @param {Function} props.onComplete - Callback when assessment is completed
 * @param {Function} props.onSystemMessage - Callback to add system messages
 * @param {Function} props.triggerAIAfterGoalAction - Callback to trigger AI response
 * @param {Object} props.platformSettings - Platform TTS settings
 * @param {Object} props.userSettings - User TTS settings
 * @param {Object} props.therapistSettings - Therapist TTS settings
 * @param {string} props.clientId - Client ID for voice selection
 * @param {boolean} props.autoplayEnabled - Whether autoplay is enabled
 * @returns {JSX.Element} AssessmentManager component
 */
const AssessmentManager = React.forwardRef(({
  token,
  currentConversationId,
  onComplete,
  onSystemMessage,
  triggerAIAfterGoalAction,
  pauseAutoplay,
  resumeAutoplay,
  platformSettings,
  userSettings,
  therapistSettings,
  clientId,
  autoplayEnabled,
  chatPhase
}, ref) => {
  // Assessment States
  const [assessmentLauncherOpen, setAssessmentLauncherOpen] = useState(false);
  const [assessmentRunnerOpen, setAssessmentRunnerOpen] = useState(false);
  const [currentAssessmentInfo, setCurrentAssessmentInfo] = useState(null);
  const [pendingAiAssessmentSuggestion, setPendingAiAssessmentSuggestion] = useState(null);
  
  // Track last assessment to prevent duplicates
  const lastAssessmentRef = useRef(null);
  // Track whether the runner has already submitted successfully
  const submissionCompleteRef = useRef(false);
  // Track whether a submission is currently in-flight (submitted, awaiting the
  // server's confirmation through the retry loop). A close during this window
  // must NOT emit "Assessment cancelled" — the submit is pending, not abandoned.
  const submissionInFlightRef = useRef(false);

  // Assessment Event Handlers
  const handleLaunchAssessment = (testKey, testName, testDescription, initiatedBy) => {
    // Check if this is the same assessment as last time (prevent duplicates)
    if (lastAssessmentRef.current && 
        lastAssessmentRef.current.testKey === testKey &&
        (Date.now() - lastAssessmentRef.current.timestamp) < 1000) {
      console.log('Ignoring duplicate assessment launch within 1 second');
      return;
    }
    
    lastAssessmentRef.current = {
      testKey: testKey,
      timestamp: Date.now()
    };
    
    setCurrentAssessmentInfo({ testKey, testName, testDescription, initiatedBy });
    setAssessmentLauncherOpen(true);
    if (pauseAutoplay) pauseAutoplay('assessment-launcher');
  };

  const handleStartAssessmentRunner = () => {
    setAssessmentLauncherOpen(false);
    if (resumeAutoplay) resumeAutoplay('assessment-launcher');
    submissionCompleteRef.current = false;
    submissionInFlightRef.current = false;
    setAssessmentRunnerOpen(true);
    if (pauseAutoplay) pauseAutoplay('assessment-runner');
  };

  const handleCloseAssessmentLauncher = (action = null) => {
    setAssessmentLauncherOpen(false);
    setPendingAiAssessmentSuggestion(null);
    if (resumeAutoplay) resumeAutoplay('assessment-launcher');

    if (action === 'rejected') {
      const rejectionMessage = {
        senderRole: 'system',
        text: `You chose not to start the ${currentAssessmentInfo?.testName || 'assessment'} at this time.`,
        timestamp: new Date()
      };
      onSystemMessage(rejectionMessage);

      // Trigger AI follow-up so the persona can respond to the decline
      if (triggerAIAfterGoalAction) {
        triggerAIAfterGoalAction(
          '[SYSTEM: The client was offered the assessment and declined. Acknowledge their choice warmly in 1-2 sentences. Do NOT call any assessment functions or suggest the assessment again.]',
          { isInternal: true }
        );
      }
    } else {
      // "Maybe Later" / backdrop dismiss / escape key — add a system message and trigger AI follow-up
      // Note: onClick={onClose} passes the MouseEvent as 'action', not null
      const deferMessage = {
        senderRole: 'system',
        text: `You chose to defer the ${currentAssessmentInfo?.testName || 'assessment'} for now.`,
        timestamp: new Date()
      };
      onSystemMessage(deferMessage);

      if (triggerAIAfterGoalAction) {
        triggerAIAfterGoalAction(
          '[SYSTEM: The client was offered the assessment and chose "maybe later". Acknowledge warmly in 1-2 sentences. Let them know they can do it whenever ready. Do NOT call any assessment functions or re-suggest the assessment.]',
          { isInternal: true }
        );
      }
    }

    setCurrentAssessmentInfo(null);
  };

  const handleAssessmentComplete = (result) => {
    submissionCompleteRef.current = true;
    setAssessmentRunnerOpen(false);
    setCurrentAssessmentInfo(null);
    if (resumeAutoplay) resumeAutoplay('assessment-runner');

    // Simple completion message without assessment details
    // Assessment results are available via context to AI therapist
    // Note: We don't include assessment name as some names could be disturbing to clients
    const completionMessage = {
      senderRole: 'system',
      text: `You completed an assessment.`,
      timestamp: new Date()
    };

    onSystemMessage(completionMessage);

    // During onboarding, the socket room may not be joined yet (race condition),
    // so we explicitly trigger a follow-up onboarding message to continue the flow.
    // In main chat phase, the server auto-generates the AI response via socket.
    if (chatPhase === 'onboarding' && triggerAIAfterGoalAction) {
      console.log('[AssessmentManager] Onboarding assessment complete — triggering follow-up');
      triggerAIAfterGoalAction('I have completed the assessment.', { isInternal: true });
    }

    if (onComplete) {
      onComplete(result);
    }
  };

  const handleCloseAssessmentRunner = () => {
    // If the assessment was already submitted successfully, treat close as completion, not cancellation
    if (submissionCompleteRef.current) {
      handleAssessmentComplete(null);
      return;
    }

    // A submission is in-flight (the user submitted; the server hasn't confirmed
    // yet through the retry loop). Close the dialog quietly — do NOT emit
    // "Assessment cancelled", which would be wrong the moment the submit lands
    // (Tom ticket). The success/completion path handles the user-facing message.
    if (submissionInFlightRef.current) {
      setAssessmentRunnerOpen(false);
      setCurrentAssessmentInfo(null);
      if (resumeAutoplay) resumeAutoplay('assessment-runner');
      return;
    }

    setAssessmentRunnerOpen(false);
    setCurrentAssessmentInfo(null);
    if (resumeAutoplay) resumeAutoplay('assessment-runner');

    const cancelMessage = {
      senderRole: 'system',
      text: 'Assessment cancelled.',
      timestamp: new Date()
    };

    onSystemMessage(cancelMessage);

    // Trigger AI follow-up so the persona can respond to the cancellation
    if (triggerAIAfterGoalAction) {
      triggerAIAfterGoalAction(
        '[SYSTEM: The client started the assessment but cancelled before finishing. Acknowledge warmly. Do NOT call any assessment functions or re-suggest the assessment.]',
        { isInternal: true }
      );
    }
  };

  // Expose methods to parent component
  React.useImperativeHandle(ref, () => ({
    launchAssessment: handleLaunchAssessment,
    handleTherapistAssessmentRequest: (data) => {
      if (data.conversationId === currentConversationId) {
        setCurrentAssessmentInfo({
          testKey: data.testKey,
          testName: data.testName,
          testDescription: data.testDescription,
          initiatedBy: 'therapist'
        });
        setAssessmentLauncherOpen(true);
      }
    },
    setPendingAiAssessmentSuggestion,
    setAssessmentLauncherOpen: (open) => {
      setAssessmentLauncherOpen(open);
      if (open) {
        // If we have a pending AI suggestion, use it
        if (pendingAiAssessmentSuggestion) {
          setCurrentAssessmentInfo({
            ...pendingAiAssessmentSuggestion,
            initiatedBy: 'ai'
          });
        }
      }
    }
  }));

  return (
    <>
      {/* Assessment Launcher */}
      {assessmentLauncherOpen && currentAssessmentInfo && (
        <AssessmentLauncher
          isOpen={assessmentLauncherOpen}
          onClose={handleCloseAssessmentLauncher}
          testName={currentAssessmentInfo.testName}
          testDescription={currentAssessmentInfo.testDescription}
          onStartAssessment={handleStartAssessmentRunner}
          initiatedBy={currentAssessmentInfo.initiatedBy}
        />
      )}

      {/* Assessment Runner */}
      <Dialog
        open={assessmentRunnerOpen}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleCloseAssessmentRunner();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          {currentAssessmentInfo?.testName || "Assessment"}
          <IconButton
            aria-label="close"
            onClick={handleCloseAssessmentRunner}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {assessmentRunnerOpen && currentAssessmentInfo && (
            // ErrorBoundary catches render errors so a failed assessment modal
            // doesn't leave a blank dialog. The key={testKey} forces React to
            // unmount/remount cleanly between assessments — prevents stale
            // state from a previous (broken) assessment leaking into the next.
            <ErrorBoundary key={currentAssessmentInfo.testKey}>
              <AssessmentRunner
                key={currentAssessmentInfo.testKey}
                token={token}
                testKey={currentAssessmentInfo.testKey}
                conversationId={currentConversationId}
                initiatedBy={currentAssessmentInfo.initiatedBy}
                onComplete={handleAssessmentComplete}
                onCloseRequest={handleCloseAssessmentRunner}
                onSubmitStart={() => {
                  // Submission is in-flight — see submissionInFlightRef above.
                  submissionInFlightRef.current = true;
                }}
                onSubmitError={() => {
                  // Submission failed (all retries exhausted). No longer in-flight,
                  // so a subsequent close is a genuine cancel again.
                  submissionInFlightRef.current = false;
                }}
                onSubmissionSuccess={() => {
                  // Adult-7 fix: mark the assessment as successfully submitted
                  // as soon as the server confirms, so any subsequent close
                  // path (X button, Escape, backdrop) is treated as completion
                  // rather than cancellation. handleAssessmentComplete still
                  // runs when the user clicks the inner "Close" button on the
                  // post-submit "Assessment Complete" sub-dialog.
                  submissionCompleteRef.current = true;
                  submissionInFlightRef.current = false;
                }}
                platformSettings={platformSettings}
                userSettings={userSettings}
                therapistSettings={therapistSettings}
                clientId={clientId}
                autoplayEnabled={autoplayEnabled}
              />
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default AssessmentManager;